import { useParams } from "react-router";
import {
  Box,
  Checkbox,
  Heading,
  useColorModeValue,
  Container,
  Flex,
  Spinner,
  Stack,
  Spacer,
  Icon,
  useToast,
} from "@chakra-ui/react";
import { MdDelete } from "react-icons/md";
import { useMutation, useQuery, useQueryClient } from "react-query";

// local imports
import CreateTodoForm from "../components/CreateTodoForm";
import { getListById, toggleTodo, deleteTodo } from "../services/firebase";

const Todo = ({ handleDelete, handleTodoChange, todoTitle, isCompleted }) => {
  const bgColor = useColorModeValue("gray.100", "gray.700");

  return (
    <Flex bg={bgColor} px={5} py={3} borderRadius="md" boxShadow="md">
      <Box fontWeight="bold" fontSize="lg" as={isCompleted ? "s" : ""}>
        <Checkbox
          spacing="1rem"
          onChange={(e) => {
            handleTodoChange(todoTitle, e.target.checked);
          }}
          isChecked={isCompleted}
        >
          {todoTitle}
        </Checkbox>
      </Box>
      <Spacer />
      <Icon
        as={MdDelete}
        _hover={{ color: "red.400" }}
        w={6}
        h={6}
        cursor="pointer"
        onClick={() => handleDelete(todoTitle)}
      />
    </Flex>
  );
};

const ListDetail = () => {
  const params = useParams();
  const listId = params.listId;
  const queryClient = useQueryClient();
  const toast = useToast();

  const {
    data: todos,
    error,
    isLoading,
  } = useQuery(["list-detail", listId], async () => {
    const list = await getListById(listId);
    return list.todos;
  });

  const deleteTodoMutation = useMutation(
    (todoTitle) => deleteTodo(listId, todoTitle),
    {
      onMutate: async (todoTitle) => {
        await queryClient.cancelQueries(["list-detail", listId]);
        const oldListData = queryClient.getQueryData(["list-detail", listId]);

        queryClient.setQueryData(["list-detail", listId], (prevData) => {
          const filteredTodos = {};
          for (const key in prevData) {
            if (prevData.hasOwnProperty(key) && key !== todoTitle) {
              filteredTodos[key] = todos[key];
            }
          }
          return filteredTodos;
        });

        return { oldListData };
      },
      onSettled: () => {
        queryClient.invalidateQueries(["list-detail", listId]);
      },
    }
  );

  const toggleTodoMutation = useMutation(
    ({ todoTitle, isChecked }) => {
      return toggleTodo(listId, todoTitle, isChecked);
    },
    {
      onMutate: async ({ todoTitle, isChecked }) => {
        await queryClient.cancelQueries(["list-detail", listId]);
        const oldListData = queryClient.getQueryData(["list-detail", listId]);

        queryClient.setQueryData(["list-detail", listId], (prevData) => {
          const prevDataCopy = { ...prevData };

          for (const key in prevDataCopy) {
            if (prevDataCopy.hasOwnProperty(key) && key === todoTitle) {
              prevDataCopy[key].completed = isChecked;
            }
          }
          return prevDataCopy;
        });

        return { oldListData };
      },
      onSettled: () => {
        queryClient.invalidateQueries(["list-detail", listId]);
      },
    }
  );

  const handleDelete = (todoTitle) => {
    deleteTodoMutation.mutate(todoTitle);
    toast({
      position: "top",
      title: "A todo was deleted",
      isClosable: true,
      duration: 3000,
      status: "warning",
    });
  };

  const handleTodoChange = (todoTitle, isChecked) => {
    toggleTodoMutation.mutate({ todoTitle, isChecked });

    toast({
      position: "top",
      title: `Marked todo as ${isChecked ? "completed" : "not completed"}`,
      isClosable: true,
      duration: 3000,
      status: "info",
    });
  };

  return (
    <Container>
      <Stack direction="column" spacing={3}>
        <CreateTodoForm listId={listId} />
        {isLoading ? (
          <Box textAlign="center">
            <Spinner thickness="4px" color="teal.500" size="lg" />
          </Box>
        ) : (
          ""
        )}

        {error ? <Heading textAlign="center">{error}</Heading> : ""}

        {!isLoading && Object.keys(todos).length === 0 && (
          <Box pt={8} textAlign="center">
            <Heading size="md">
              No Todos in this list, start by creating one.
            </Heading>
          </Box>
        )}

        {todos &&
          Object.keys(todos).map((todoTitle) => (
            <Todo
              key={todoTitle}
              todoTitle={todoTitle}
              isCompleted={todos[todoTitle].completed}
              handleDelete={handleDelete}
              handleTodoChange={handleTodoChange}
            />
          ))}
      </Stack>
    </Container>
  );
};

export default ListDetail;
